import React, { useEffect, useMemo, useState } from 'react';

import * as CatalogueSearch from '@/core/components/CatalogueSearch';
import Icons from '@/core/components/Icons';
import { LocationInput } from '@/core/components/inputs/CityInput/LocationInput';
import SelectInput from '@/core/components/inputs/SelectInput';
import { useComponentsContext } from '@/core/lib/components/components.context';
import { useNativeContext } from '@/core/lib/native/native.context';
import Store from '@/core/lib/new-architecture/store';
import { useCountThematique, useThematique } from '@/core/lib/new-architecture/store/thematique.store';
import { usePersistContext } from '@/core/lib/persist/persist.context';
import { CatalogueParams } from '@/core/lib/router/common.route';
import { useRouterContext } from '@/core/lib/router/router.context';
import Routes from '@/core/lib/router/routes';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import ItemList from '@/core/pages/Catalogue/ItemList';
import type { PageWithLayoutsAndProviders } from '@/core/pages/page.types';
import { QuerySortEnum } from '@/core/types/donation';
import { LocationForm } from '@/core/types/geo';

const Thematique: PageWithLayoutsAndProviders = () => {
  const { t } = useTranslationContext(['common', 'catalogue']);
  const { isNative } = useNativeContext();
  const { data: current } = Store.current.useCurrent();
  const { Page, Content, MetaTitle } = useComponentsContext();
  const { query } = useRouterContext();
  const { setSession, getSession, removeSession } = usePersistContext();

  const [values, setValues] = useState<Values>({ loc: null });

  const onValuesChange = (newValues: Partial<Values>) => {
    setValues(old => ({ ...old, ...newValues }));
    if (newValues.loc) setSession('loc', JSON.stringify(newValues.loc));
    else removeSession('loc');
  };

  const getLocSession = async () => {
    const loc = await getSession('loc');
    return loc ? JSON.parse(loc) : null;
  };

  useEffect(() => {
    getLocSession().then(locSession => {
      const locCurrent = current?.getLoc()?.getLocationForm();
      const locToUse = locSession ?? (locCurrent ? { ...locCurrent, distance: 100 } : null);
      if (locToUse) setValues({ loc: locToUse });
    });
  }, [current]);

  const sort: QuerySortEnum = values.loc ? (query.sort as QuerySortEnum) : QuerySortEnum.DATE;

  const { data: catalogue, next, isLoading, total, notFound } = useThematique(query.thematiqueId, { sort, loc: values.loc?.loc, distance: values.loc?.distance, page: Number(query.page) });

  const { onGlobalNotFound } = useRouterContext();

  if (notFound && !isLoading) {
    onGlobalNotFound?.();
    return;
  }

  const routeParams = Routes.CatalogueRoute.useParams();
  const catalogueParams = { ...routeParams.page, ...routeParams.search, tag: query.thematiqueId, sort, loc: values.loc?.loc };

  return (
    <>
      <MetaTitle
        meta={{
          title: `Dons - ${t(`thematiques.${query.thematiqueId}.title`, { ns: 'common' })}`,
          description: t(`thematiques.${query.thematiqueId}.description`, { ns: 'common' }),
          image: '/assets/sigle.png',
        }}
      />

      <Page noPadding>
        <Content scroll={total > 0 ? `${catalogue?.getRoute().resolve()}/loc=${values.loc?.loc ?? 'null'}` : undefined}>
          {isNative && (
            <div className="sticky top-0 z-50 flex w-full bg-bg-primary px-4 pt-safe-top">
              <div className="flex w-full py-4">
                <CatalogueSearch.CatalogueSearchButton />
              </div>
            </div>
          )}
          <div className={isNative ? 'px-4' : ''}>
            <ThematiqueHeader thematiqueId={query.thematiqueId} values={values} onChange={onValuesChange} catalogueParams={catalogueParams} sort={sort} />
          </div>
          {isLoading ? (
            <div aria-label={t('spinner.aria-valuetext', { ns: 'catalogue' })} className="flex h-screen items-center justify-center">
              <Icons icon="spinner" size="40" color="button-default" />
            </div>
          ) : (
            catalogue?.donations && <ItemList donations={catalogue.donations} search={catalogue.search} next={next} />
          )}
          {!isNative && (
            <div className="mb-20 mt-5 pt-safe-top">
              <CatalogueSearch.CataloguePagination total={total} thematiqueId={query.thematiqueId} />
            </div>
          )}
        </Content>
      </Page>

      {isNative && <CatalogueSearch.CatalogueSearchDialog />}
    </>
  );
};

type Values = { loc: LocationForm | null };

const ThematiqueHeader: React.FC<{ thematiqueId: string; values: Values; onChange: (values: Partial<Values>) => void; catalogueParams: Partial<CatalogueParams>; sort: QuerySortEnum }> = ({
  thematiqueId,
  values,
  onChange,
  catalogueParams,
  sort,
}) => {
  const { t } = useTranslationContext(['common']);
  const { isNative } = useNativeContext();
  const { query, push } = useRouterContext();
  const [isDisplayFullDescription, setDisplayFullDescription] = useState<boolean>(false);

  const { data: catalogue, total } = useThematique(query.thematiqueId, { sort, loc: values.loc?.loc, distance: values.loc?.distance });
  const {
    search: { page = 1, size },
  } = Routes.CatalogueRoute.useParams();

  const results = useMemo(() => {
    if (total === 0) return t('search.recap.seo-zero', { ns: 'common' });
    if (isNative) return t('search.recap.seo-native', { total: total.toString(), ns: 'common' });

    return t('search.recap.seo', { total: total.toString(), page: page.toString(), nPage: Math.ceil(total / size).toString(), ns: 'common' });
  }, [total, page, size, isNative]);

  if (!catalogue) {
    return null;
  }

  const handleOnDisplayFullDescription = () => {
    setDisplayFullDescription(!isDisplayFullDescription);
  };

  return (
    <div className="flex w-full flex-col gap-1 border-stroke-tertiary pb-4 lg:border-b lg:pb-5">
      <h1 className="font-semibold text-content-secondary">{t(`thematiques.${thematiqueId}.title`, { ns: 'common' })}</h1>
      {!isNative && (
        <>
          <p className={`text-body-secondary text-content-primary ${isDisplayFullDescription ? 'line-clamp-none' : 'max-lg:line-clamp-1'}`}>
            {t(`thematiques.${thematiqueId}.description`, { ns: 'common' })}
          </p>
          <button type="button" onClick={handleOnDisplayFullDescription} className="text-start underline lg:hidden">
            {t(isDisplayFullDescription ? 'thematiques.see-less' : 'thematiques.see-more', { ns: 'common' })}
          </button>
        </>
      )}
      <div className="my-3 md:w-1/2 lg:my-5">
        <ThematiqueLocationInput values={values} onChange={onChange} />
      </div>
      <div className="grid grid-cols-[1fr_auto]">
        <p className="text-caption-primary mt-2 shrink-0 text-content-placeholder">{results}</p>
        <div className="relative w-[153px]">
          <SelectInput
            id="sort"
            items={[
              { value: QuerySortEnum.DATE, text: t('thematiques.label-date', { ns: 'common' }) },
              { value: QuerySortEnum.DISTANCE, text: t('thematiques.label-distance', { ns: 'common' }) },
            ]}
            onChange={sort => push(new Routes.CatalogueRoute({ ...catalogueParams, sort: sort || undefined }))}
            value={sort}
            hideCleanIcon
            isDisabled={!values.loc}
            isHidden={!values.loc}
          />
          {values.loc && <Icons icon="chevron-down" className="pointer-events-none absolute right-2 top-2" />}
        </div>
      </div>
    </div>
  );
};

const ThematiqueLocationInput: React.FC<{ values: Values; onChange: (values: Partial<Values>) => void }> = ({ values, onChange }) => {
  const { t } = useTranslationContext(['common']);
  const [innerValue, setInnerValue] = useState<Values>(values);
  const { query } = useRouterContext();
  const { data, isLoading } = useCountThematique({ loc: innerValue.loc, tag: query.thematiqueId });

  return (
    <LocationInput
      id="loc"
      locationInputType="page"
      value={innerValue.loc || null}
      onChange={loc => setInnerValue(old => ({ ...old, loc }))}
      onClose={loc => {
        onChange({ loc: loc || null });
      }}
      onClean={() => {
        setInnerValue({ loc: null });
        onChange({ loc: null });
      }}
      placeholder={t('thematiques.placeholder-loc', { ns: 'common' })}
      validateText={t('thematiques.validate-text-loc', { ns: 'common' })}
      count={data?.getTotal()}
      loading={isLoading}
      submitIsDisabled={isLoading}
    />
  );
};

export default Thematique;
